// Editor header chip: when the record was last checked, and when it's due again
import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { cn } from "@/lib/utils"
import { BadgeCheck, CalendarClock } from "lucide-react"
import type { Person, RecordMeta } from "../persistence/recordMeta"
import { ago, DEFAULT_REVIEW_MONTHS, inTime, type Freshness } from "../useRecordMeta"

const INTERVALS = [
  { months: 3, label: "Every 3 months" },
  { months: 6, label: "Every 6 months" },
  { months: 12, label: "Every year" },
  { months: 0, label: "Never" },
]

export function FreshnessChip({ freshness, meta, people, canEdit, onUpdate }: {
  freshness: Freshness | null
  meta: RecordMeta | undefined
  people: Person[]
  canEdit: boolean
  onUpdate: (patch: Partial<RecordMeta>) => void
}) {
  const [open, setOpen] = useState(false)
  const months = meta?.reviewEveryMonths ?? DEFAULT_REVIEW_MONTHS
  const state = freshness?.state ?? "ok"

  const label = !freshness ? "No review schedule"
    : state === "overdue" ? "Review overdue"
      : state === "soon" ? `Review due ${inTime(freshness.dueAt)}`
        : `Checked ${ago(freshness.checkedAt)}`

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title={freshness ? `Last checked ${new Date(freshness.checkedAt).toLocaleDateString()}. Next review ${inTime(freshness.dueAt)}.` : "This record isn't scheduled for review"}
          className={cn(
            "inline-flex shrink-0 items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-medium transition-colors",
            state === "overdue" && "border-red-200 bg-red-50 text-red-700 hover:bg-red-100 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300",
            state === "soon" && "border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200",
            state === "ok" && "text-muted-foreground hover:bg-muted",
          )}
        >
          <CalendarClock className="h-2.5 w-2.5" />
          {label}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 space-y-3 p-3">
        <div>
          <p className="text-xs font-semibold">Accuracy review</p>
          <p className="text-[11px] text-muted-foreground">
            {freshness
              ? `Last checked ${ago(freshness.checkedAt)}${meta?.verifiedBy && meta.verifiedAt === freshness.checkedAt ? ` by ${meta.verifiedBy}` : ""}. Next review ${inTime(freshness.dueAt)}.`
              : "Not scheduled for review."}
            {" "}Saving it counts as a check.
          </p>
        </div>

        <label className="block space-y-1">
          <span className="text-[11px] font-medium">Owner</span>
          <Select
            value={meta?.owner?.id ?? "none"}
            disabled={!canEdit}
            onValueChange={v => onUpdate({ owner: v === "none" ? undefined : people.find(p => p.id === v) })}
          >
            <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="No owner" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none" className="text-xs">No owner</SelectItem>
              {people.map(p => <SelectItem key={p.id} value={p.id} className="text-xs">{p.name}</SelectItem>)}
              {meta?.owner && !people.some(p => p.id === meta.owner!.id) && (
                <SelectItem value={meta.owner.id} className="text-xs">{meta.owner.name}</SelectItem>
              )}
            </SelectContent>
          </Select>
        </label>

        <label className="block space-y-1">
          <span className="text-[11px] font-medium">Review</span>
          <Select
            value={String(months)}
            disabled={!canEdit}
            onValueChange={v => onUpdate({ reviewEveryMonths: Number(v) === DEFAULT_REVIEW_MONTHS ? undefined : Number(v) })}
          >
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              {INTERVALS.map(i => <SelectItem key={i.months} value={String(i.months)} className="text-xs">{i.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </label>

        {canEdit && freshness && (
          <Button size="sm" className="h-8 w-full gap-1.5 text-xs" onClick={() => { onUpdate({ verifiedAt: Date.now() }); setOpen(false) }}>
            <BadgeCheck className="h-3.5 w-3.5" /> Mark as verified
          </Button>
        )}
      </PopoverContent>
    </Popover>
  )
}
